import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isApiError } from "@magicweave/api";
import type {
  DjinnGameThread,
  DjinnPageContext,
  DjinnPosted,
  DjinnReply,
} from "@magicweave/api";

import { unwrap } from "../api/shell-hooks";
import { useApi } from "../api/context";

/** How often the open sheet re-reads a thread while Djinn is working on it. */
export const DJINN_POLL_MS = 1500;

/**
 * What the server answers, with a 503, when staff have turned Djinn off for the studio: its words and
 * the hint that says who can turn it on. Anything else, a 503 from a stack that is down included, is
 * not this: it is a failure to read and keeps its retry.
 */
export interface DjinnOff {
  message: string;
  hint?: string;
}

export function djinnOff(err: unknown): DjinnOff | undefined {
  if (!isApiError(err) || err.status !== 503 || !/isn.t on\b/i.test(err.message))
    return undefined;
  return { message: err.message, hint: err.hint };
}

/**
 * A Djinn that is off stays off until staff turn it on: asking again changes nothing, so it is never
 * retried. Every other failure keeps whatever retry the app's client gives its reads.
 */
export function useRetryUnlessOff() {
  const qc = useQueryClient();
  return useCallback(
    (failures: number, err: Error): boolean => {
      if (djinnOff(err) !== undefined) return false;
      const base = qc.getDefaultOptions().queries?.retry;
      if (typeof base === "function") return base(failures, err);
      if (typeof base === "number") return failures < base;
      return base ?? failures < 3;
    },
    [qc],
  );
}

const gameThreadKey = (projectId: string) =>
  ["project", projectId, "djinn", "game-thread"] as const;

/** Everything the game's one thread has loaded: the latest page, and any earlier ones the person asked for. */
export type GameThread = DjinnGameThread;

/**
 * What a question and a fix cost, read once from the game's thread (the same cache the sheet polls,
 * not polled here): the home page shows them beside its rows. Undefined until the read comes back.
 */
export function useDjinnPrices(projectId: string): { answer: number; fix: number } | undefined {
  const api = useApi();
  const read = useQuery({
    queryKey: gameThreadKey(projectId),
    queryFn: async (): Promise<GameThread> =>
      unwrap(
        await api.GET("/v1/projects/{projectId}/djinn/thread", {
          params: { path: { projectId } },
        }),
      ),
    enabled: Boolean(projectId),
    staleTime: 60_000,
  });
  return read.data?.prices;
}

/**
 * The game's one thread, re-read every 1.5 s while the sheet is open and not at all
 * while it is closed, because a poll nobody can see is a poll somebody pays for.
 * `loadOlder(before)` reads the page before that message's `seq` and folds it in.
 */
export function useDjinnGameThread(projectId: string, { open }: { open: boolean }) {
  const api = useApi();
  const retry = useRetryUnlessOff();
  const [older, setOlder] = useState<{
    thread: GameThread[];
    loading: boolean;
    failed: boolean;
  }>({
    thread: [],
    loading: false,
    failed: false,
  });
  const latest = useQuery({
    queryKey: gameThreadKey(projectId),
    queryFn: async (): Promise<GameThread> =>
      unwrap(
        await api.GET("/v1/projects/{projectId}/djinn/thread", {
          params: { path: { projectId } },
        }),
      ),
    enabled: open && Boolean(projectId),
    retry,
    refetchInterval: (query) =>
      open && djinnOff(query.state.error) === undefined ? DJINN_POLL_MS : false,
  });
  const off = djinnOff(latest.error);

  const loadOlder = useCallback(
    async (before: number) => {
      setOlder((o) => ({ ...o, loading: true, failed: false }));
      try {
        const page = unwrap(
          await api.GET("/v1/projects/{projectId}/djinn/thread", {
            params: { path: { projectId }, query: { before } },
          }),
        );
        setOlder((o) => ({ thread: [page, ...o.thread], loading: false, failed: false }));
      } catch {
        setOlder((o) => ({ ...o, loading: false, failed: true }));
      }
    },
    [api, projectId],
  );

  const merged = latest.data ? mergeThreadPages(older.thread, latest.data) : undefined;
  return {
    thread: merged,
    readAt: latest.dataUpdatedAt,
    isPending: latest.isPending,
    isError: latest.isError,
    off,
    refetch: latest.refetch,
    loadOlder,
    loadingOlder: older.loading,
    olderFailed: older.failed,
  };
}

/** Pages oldest first, then the latest; a message or reply on two pages is kept once, from the newer read. */
export function mergeThreadPages(older: GameThread[], latest: GameThread): GameThread {
  const messages = new Map<string, GameThread["messages"][number]>();
  const replies = new Map<string, DjinnReply>();
  for (const page of [...older, latest]) {
    for (const m of page.messages) messages.set(m.id, m);
    for (const r of page.replies) replies.set(r.id, r);
  }
  const first = older[0] ?? latest;
  return {
    ...latest,
    messages: [...messages.values()].sort((a, b) => a.seq - b.seq),
    replies: [...replies.values()].sort(
      (a, b) =>
        Date.parse(a.started_at) - Date.parse(b.started_at) || a.after_seq - b.after_seq,
    ),
    has_more_before: first.has_more_before,
  };
}

/** What a message to the game's thread carries. */
export interface DjinnThreadSend {
  /** Reused by a retry: the server answers a repeated id with the first send. */
  messageId: string;
  body: string;
  context?: DjinnPageContext;
  choice?: { replyId: string; option: string };
}

/** Posts to the game's one thread. Decide reads the message; the answer comes back on the next read. */
export function usePostDjinnThreadMessage(projectId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (send: DjinnThreadSend): Promise<DjinnPosted> =>
      unwrap(
        await api.POST("/v1/projects/{projectId}/djinn/thread/messages", {
          params: { path: { projectId } },
          body: {
            message_id: send.messageId,
            body: send.body,
            context: send.context,
            choice: send.choice
              ? { reply_id: send.choice.replyId, option: send.choice.option }
              : undefined,
            via: "console",
          },
        }),
      ),
    onSettled: () =>
      Promise.all([qc.invalidateQueries({ queryKey: gameThreadKey(projectId) })]),
  });
}

/** Stops a running fix: the one who started it, or an admin. Anyone else is told so by the server. */
export function useStopDjinnReply(projectId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (replyId: string): Promise<DjinnReply> =>
      unwrap(
        await api.POST("/v1/projects/{projectId}/djinn/replies/{replyId}/stop", {
          params: { path: { projectId, replyId } },
        }),
      ),
    onSettled: () =>
      Promise.all([qc.invalidateQueries({ queryKey: gameThreadKey(projectId) })]),
  });
}

/** One reply, read alone: the lamp's peek reads the finding it is about to name. */
export function useDjinnReply(
  projectId: string,
  replyId: string | undefined,
  enabled: boolean,
) {
  const api = useApi();
  return useQuery({
    queryKey: ["project", projectId, "djinn", "reply", replyId] as const,
    queryFn: async () =>
      unwrap(
        await api.GET("/v1/projects/{projectId}/djinn/replies/{replyId}", {
          params: { path: { projectId, replyId: replyId ?? "" } },
        }),
      ).reply,
    enabled: enabled && Boolean(projectId) && Boolean(replyId),
    staleTime: 60_000,
    retry: false,
  });
}

/** The figure the player field draws, one value per player. */
export type FieldMetric = "earned_per_spent" | "earned" | "spent";

/**
 * Each player's value for a currency in a game (at most 400, sampled), read once per figure and
 * kept for a minute: the dots are the same on every read of the same players, so a re-read
 * never makes the field jump. `enabled` is false until a card names a currency.
 */
export function useDjinnField(
  projectId: string,
  {
    currency,
    metric = "earned_per_spent",
    environmentId,
    until,
    enabled = true,
  }: {
    currency: string | undefined;
    metric?: FieldMetric;
    environmentId?: string;
    /** The field as it stood at this moment (an ISO time), such as when a release went out. */
    until?: string;
    enabled?: boolean;
  },
) {
  const api = useApi();
  return useQuery({
    queryKey: [
      "project",
      projectId,
      "djinn",
      "field",
      currency,
      metric,
      environmentId,
      until,
    ] as const,
    queryFn: async () =>
      unwrap(
        await api.GET("/v1/projects/{projectId}/djinn/field", {
          params: {
            path: { projectId },
            query: {
              currency: currency ?? "",
              metric,
              environment_id: environmentId,
              until,
            },
          },
        }),
      ),
    enabled: enabled && Boolean(projectId) && Boolean(currency),
    staleTime: 60_000,
    retry: false,
  });
}
